// Centralised fetch helper for the admin panel.
// Reads the admin password from localStorage and sends it as X-Admin-Password.
// On 401, clears the session and returns a uniform error envelope.
import { API_BASE } from "../../lib/api";

const SESSION_KEY = "admin_session";
const TOKEN_KEY   = "admin_token";

export function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.value || obj.value !== "valid") return null;
    if (!obj?.expiry || Date.now() > obj.expiry) return null;
    return obj;
  } catch (_e) {
    return null;
  }
}

export function saveSession(token) {
  const session = { value: "valid", expiry: Date.now() + 8 * 60 * 60 * 1000 };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  localStorage.setItem(TOKEN_KEY, token || "");
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

async function call(method, path, { body, isForm = false, rawText = false, timeoutMs } = {}) {
  const headers = { "X-Admin-Password": getToken() };
  let payload;
  if (body !== undefined) {
    if (isForm) {
      payload = body; // FormData
    } else {
      headers["Content-Type"] = "application/json";
      payload = JSON.stringify(body);
    }
  }
  const controller = timeoutMs ? new AbortController() : null;
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${API_BASE}${path}`, { method, headers, body: payload, signal: controller?.signal });
    if (res.status === 401) {
      // Don't auto-redirect on the login call itself — let the caller surface
      // the error so the user sees "Invalid credentials" instead of a page flash.
      if (!path.endsWith("/admin/login")) {
        clearSession();
        window.location.href = "/admin";
        return { ok: false, status: 401, error: "Session expired. Please sign in again." };
      }
    }
    if (rawText) {
      const text = await res.text().catch(() => "");
      if (!res.ok) return { ok: false, status: res.status, error: text || "Request failed.", data: text };
      return { ok: true, status: res.status, data: text };
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, status: res.status, error: data?.detail || "Request failed.", data };
    return { ok: true, status: res.status, data };
  } catch (_e) {
    if (_e?.name === "AbortError") return { ok: false, status: 0, error: "Request timed out. Please try again." };
    return { ok: false, status: 0, error: "Network error. Please try again." };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const adminApi = {
  login: (username, password) =>
    call("POST", "/admin/login", { body: { username, password } }),

  stats:                      () => call("GET", "/admin/stats"),
  listCustomers: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.search) q.set("search", params.search);
    if (params.include_deleted) q.set("include_deleted", "true");
    const qs = q.toString();
    return call("GET", `/admin/customers${qs ? `?${qs}` : ""}`);
  },

  // Customer soft-delete + restore (founder-OTP authorized)
  deleteRequest:  (id) => call("POST", `/admin/customers/${id}/delete-request`),
  deleteConfirm:  (id, otp) => call("POST", `/admin/customers/${id}/delete-confirm`, { body: { otp } }),
  restoreRequest: (id) => call("POST", `/admin/customers/${id}/restore-request`),
  restoreConfirm: (id, otp) => call("POST", `/admin/customers/${id}/restore-confirm`, { body: { otp } }),
  auditLog:       (id) => call("GET", `/admin/audit-log${id ? `?customer_id=${id}` : ""}`),

  // Manual Customer Upgrade — admin-granted entitlement override (no Razorpay)
  customerBilling: (id) => call("GET", `/admin/billing/customer/${id}`),
  manualUpgrade:   (id, body) => call("POST", `/admin/customers/${id}/upgrade`, { body, timeoutMs: 20000 }),

  // C-ORE Fund — funding advisory requests
  fundingRequests: (status) => call("GET", `/admin/funding-requests${status ? `?status=${status}` : ""}`),
  patchCustomer:   (id, patch) => call("PATCH", `/admin/customers/${id}`, { body: patch }),
  getChecklist:    (id)        => call("GET",   `/admin/checklist/${id}`),
  saveChecklist:   (customer_id, field, value) =>
    call("POST", "/admin/checklist", { body: { customer_id, field, value } }),
  customerIndicators: (id)     => call("GET", `/admin/customer-indicators/${id}`),
  sendWelcome:     (id)        => call("POST", "/admin/send-welcome", { body: { customer_id: id } }),
  setupComplete:   (id)        => call("POST", "/admin/setup-complete", { body: { customer_id: id } }),
  ingestBank:      (customer_id, file) => {
    const fd = new FormData();
    fd.append("customer_id", customer_id);
    fd.append("file", file);
    return call("POST", "/admin/ingest/bank-statement", { body: fd, isForm: true });
  },

  // Tally-agent endpoints (admin-protected via X-Admin-Password)
  tallyGenerateConfig: (id) => call("POST", `/tally/generate-config/${id}`),
  tallyResendEmail:    (id) => call("POST", `/tally/resend-email/${id}`),
  tallyStatus:         (id) => call("GET",  `/tally/status/${id}`),
  tallyStatusBatch:    (ids) => call("GET",  `/tally/status-batch?customer_ids=${ids.join(",")}`),
  tallyHealth:         ()   => call("GET",  `/tally/health`),
  tallyLogs:           (id) => call("GET",  `/tally/logs/${id}`),
  tallyDebugSyncQueue:  (id, step, raw_xml) =>
    call("POST", `/tally/debug-sync/${id}`, { body: { step, request_xml: raw_xml || null } }),
  tallyDebugSyncStatus: (req_id) => call("GET", `/tally/debug-sync-status/${req_id}`),
  tallyDebugSyncList:   (id)     => call("GET", `/tally/debug-sync-list/${id}`),
  tallyDiagnose:        (id)     => call("GET", `/tally/diagnose/${id}`),
  tallyDiagnoseText:    (id)     => call("GET", `/tally/diagnose/${id}?format=text`, { rawText: true }),

  // Company Parameters (stored in customers.company_parameters JSONB column)
  getCompanyParameters:    (id) => call("GET",   `/admin/company-parameters/${id}`),
  saveCompanyParameters:   (customer_id, company_parameters) =>
    call("PATCH", "/admin/company-parameters", { body: { customer_id, company_parameters } }),

  // Subscription Management — admin-managed plan catalog (versioned pricing)
  listPlanConfigs:  ()          => call("GET",   "/admin/billing/plans"),
  createPlanConfig: (body)      => call("POST",  "/admin/billing/plans", { body }),
  updatePlanConfig: (id, patch) => call("PATCH", `/admin/billing/plans/${id}`, { body: patch }),

  // C-ORE Expert — human advisory layer
  expertDashboard:    ()                    => call("GET", "/admin/expert/dashboard"),
  expertListRequests: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.category) q.set("category", params.category);
    const qs = q.toString();
    return call("GET", `/admin/expert/requests${qs ? `?${qs}` : ""}`);
  },
  expertGetRequest:      (id)          => call("GET",    `/admin/expert/requests/${id}`),
  expertPatchRequest:    (id, patch)   => call("PATCH",   `/admin/expert/requests/${id}`, { body: patch }),
  expertAssign:          (id, expertId, assignedBy) =>
    call("POST", `/admin/expert/requests/${id}/assign`, { body: { expert_id: expertId, assigned_by: assignedBy || "founder" } }),
  expertActivate:        (id, days = 30) => call("POST", `/admin/expert/requests/${id}/activate`, { body: { days } }),
  expertAttachResource:  (id, resourceId) =>
    call("POST", `/admin/expert/requests/${id}/resources`, { body: { resource_id: resourceId } }),
  expertDetachResource:  (id, resourceId) => call("DELETE", `/admin/expert/requests/${id}/resources/${resourceId}`),

  expertListExperts:  (activeOnly = false) => call("GET", `/admin/expert/experts${activeOnly ? "?active_only=true" : ""}`),
  expertCreateExpert: (body)   => call("POST",  "/admin/expert/experts", { body }),
  expertPatchExpert:  (id, patch) => call("PATCH", `/admin/expert/experts/${id}`, { body: patch }),
  expertDeleteExpert: (id)     => call("DELETE", `/admin/expert/experts/${id}`),

  expertListResources:  (activeOnly = false) => call("GET", `/admin/expert/resources${activeOnly ? "?active_only=true" : ""}`),
  expertCreateResource: (body)   => call("POST",  "/admin/expert/resources", { body }),
  expertPatchResource:  (id, patch) => call("PATCH", `/admin/expert/resources/${id}`, { body: patch }),
  expertDeleteResource: (id)     => call("DELETE", `/admin/expert/resources/${id}`),

  // Admin / Expert Custom Task Injection into Command Centre (2026-09)
  expertCustomerContext: (customerId) => call("GET", `/admin/expert/customer-context/${customerId}`),
  expertInjectTask:      (body)       => call("POST", "/admin/expert/inject-task", { body }),
  expertInjectedTasks:   (customerId) =>
    call("GET", `/admin/expert/injected-tasks${customerId ? `?customer_id=${customerId}` : ""}`),
};
